<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResponseForm from './ResponseForm.vue';

const props = defineProps({
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const route = useRoute();

const dialogRef = ref(null);
const responseForm = ref(null);
const discardDialog = ref(null);
const saving = ref(false);
const saveError = ref('');
let active = true;
onBeforeUnmount(() => {
  active = false;
});

const updateResponse = responseDetails =>
  store.dispatch('captainResponses/update', {
    id: props.selectedResponse.id,
    ...responseDetails,
  });

const i18nKey = computed(() => `CAPTAIN.RESPONSES.${props.type.toUpperCase()}`);

const createResponse = responseDetails =>
  store.dispatch('captainResponses/create', responseDetails);

const handleSubmit = async updatedResponse => {
  if (saving.value) return;
  const context = () =>
    JSON.stringify([
      route.params.accountId,
      route.params.assistantId,
      props.selectedResponse?.id,
      props.type,
    ]);
  const requestContext = context();
  const current = () => active && context() === requestContext;
  saving.value = true;
  saveError.value = '';
  try {
    if (props.type === 'edit') {
      await updateResponse({
        ...updatedResponse,
        assistant_id: route.params.assistantId,
      });
    } else {
      await createResponse({
        ...updatedResponse,
        assistant_id: route.params.assistantId,
      });
    }
    if (!current()) return;
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    if (!current()) return;
    saveError.value = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
  } finally {
    saving.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  if (saving.value) return;
  if (responseForm.value?.isDirty) {
    discardDialog.value.open();
    return;
  }
  dialogRef.value.close();
};
const discardChanges = () => {
  discardDialog.value.close();
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    confirm-before-close
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.RESPONSES.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @request-close="handleCancel"
    @confirm="responseForm?.submit()"
    @close="handleClose"
  >
    <p
      v-if="saveError"
      role="alert"
      class="mb-4 rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>
    <ResponseForm
      ref="responseForm"
      root-tag="div"
      :saving="saving"
      :mode="type"
      :response="selectedResponse"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
  <Dialog
    ref="discardDialog"
    type="alert"
    :title="t('CAPTAIN.RESPONSES.DRAFT.TITLE')"
    :description="t('CAPTAIN.RESPONSES.DRAFT.DESCRIPTION')"
    :confirm-button-label="t('CAPTAIN.RESPONSES.DRAFT.DISCARD')"
    :cancel-button-label="t('CAPTAIN.RESPONSES.DRAFT.KEEP')"
    @confirm="discardChanges"
  />
</template>
